import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

import ApiError from 'api/error'
import ButtonPrimary from 'components/button-primary'
import { HEADER_DESKTOP_HEIGHT } from 'components/layout'
import PageHeader from 'components/layout/page-header'
import NotificationHero from 'components/notification-hero'

import theme from 'theme'

type ErrorMessageMap = { title: string; message: string }
const errorCodeMessages: { [key: number]: ErrorMessageMap } = {
  400: {
    title: "We're stumped",
    message: 'Please check your internet connection or try again later.',
  },
  401: {
    title: 'Account not recognised',
    message: "We couldn't validate your credentials.",
  },
  403: {
    title: 'Caught short of the crease',
    message:
      'You do not have permission to access to this content. Please contact support if you think this is an error.',
  },
  404: {
    title: 'Absent',
    message: 'The page you are looking for was moved, removed, renamed or might never have existed.',
  },
  408: {
    title: 'Timed out',
    message: "Back to the pavilion, the server didn't send a complete response in time.",
  },
}

const ErrorBoundary = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const [isStaleChunkError, setIsStaleChunkError] = useState(false)

  if (
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed'))
  ) {
    // if user was using a stale session with old dynamic JS chunks that no longer exist...
    // ...force the user to the newest version
    setIsStaleChunkError(true)
    window.location.assign('/')
  }

  useEffect(() => {
    if (!isStaleChunkError) {
      console.error(error) // eslint-disable-line no-console
    }
  }, [error, isStaleChunkError])

  if (isStaleChunkError) return

  let errorTitle: string
  let errorMessage: string | undefined = undefined

  if (isRouteErrorResponse(error) || error instanceof ApiError) {
    // error is type `ErrorResponse`
    errorTitle = errorCodeMessages[error.status]?.title || "We're stumped"
    errorMessage =
      errorCodeMessages[error.status]?.message || "The operation couldn't be completed. Please try again later."
  } else if (error instanceof Error) {
    errorTitle = error.message
  } else if (typeof error === 'string') {
    errorTitle = error
  } else {
    errorTitle = "We're stumped"
    errorMessage = 'An unexpected error has occurred.'
  }

  return (
    <Flex w="100%" direction="column">
      <Flex w="100%" h={HEADER_DESKTOP_HEIGHT} align="center">
        <PageHeader isSimple />
      </Flex>
      <Stack
        w="100%"
        direction="column"
        spacing="16px"
        flex={{ base: 1, lg: 5 }}
        p={`${theme.constants.viewportPadding}px ${theme.constants.viewportPadding * 2}px`}
      >
        <Box>
          <ButtonPrimary
            text="Back"
            icon={{ source: ['far', 'chevron-left'] }}
            color={theme.baseColors.primary.main}
            onClick={() => navigate(-1)}
          />
        </Box>
        <NotificationHero
          icon={['far', 'circle-info']}
          status="error"
          title={`🙁 ${errorTitle}`}
          message={
            errorMessage ? (
              <Text textStyle="body1" p={0} bg="transparent">
                {errorMessage}
              </Text>
            ) : undefined
          }
        />
      </Stack>
    </Flex>
  )
}

export default ErrorBoundary
